import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./PageContent.css";
import "./GalleryPage.css";
import "./FacultyPage.css";
import BackToTopButton from "../components/BackToTopButton";

function slugify(str) {
  return String(str || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function FacultyPage() {
  // Route: /faculty/:mode?/:catSlug?
  var params = useParams();
  var modeParam = params.mode;
  var catSlug = params.catSlug;
  var navigate = useNavigate();
  var mode = modeParam === "designation" ? "designation" : "department";

  var deptResult = useApiData("faculty-departments");
  var departments = deptResult.data;
  var loadingDepts = deptResult.loading;

  var degResult = useApiData("faculty-designations");
  var designations = degResult.data;
  var loadingDegs = degResult.loading;

  var facResult = useApiData("faculty");
  var faculty = facResult.data;
  var loadingFaculty = facResult.loading;

  // Same leadership feed GalleryPage uses for the "MESSAGE FROM" sidebar
  var leadersResult = useApiData("leadership");
  var leaders = leadersResult.data;

  var loading = loadingDepts || loadingDegs || loadingFaculty;

  var principal = (leaders || []).find(function (l) {
    return /principal|dean/.test(String(l.role || "").toLowerCase());
  });
  var medicalSuperintendent = (leaders || []).find(function (l) {
    return String(l.role || "")
      .toLowerCase()
      .indexOf("medical superintendent") !== -1;
  });

  function renderOfficialMessage(leader, title) {
    if (!leader) {
      return null;
    }

    return (
      <div className="official-message-block">
        <div className="official-message-title">{title}</div>

        <div className="official-message-body">
          <div className="official-message-top">
            <div className="official-photo-wrapper">
              <img
                src={leader.img}
                alt={leader.name}
                className="official-photo"
                onError={function (e) {
                  e.target.src =
                    "https://ui-avatars.com/api/?name=" +
                    encodeURIComponent(leader.name || "Official") +
                    "&background=0b5d7a&color=fff&size=150";
                }}
              />
            </div>

            <div className="official-message-text">
              <p>{leader.message}</p>
            </div>
          </div>

          <div className="official-name">{leader.name}</div>

          <div className="official-role">{leader.role}</div>

          <div className="official-more-wrapper">
            <Link
              to={"/leadership-message/" + slugify(leader.role)}
              className="official-more"
            >
              More...
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Grouping helpers, usable for EITHER mode regardless of which mode
  // is currently active — needed so the side menu can render both the
  // Department list and the Designation list at the same time, and so
  // clicking an item can jump straight to it without waiting on a URL
  // change first.
  function getMeta(m) {
    return {
      rawList: m === "designation" ? designations : departments,
      idKey: m === "designation" ? "deg_id" : "dept_id",
      nameKey: m === "designation" ? "deg_name" : "dept_name",
      facultyIdKey: m === "designation" ? "fdesignation" : "fdepartment",
    };
  }

  function getSortedCategories(m) {
    var meta = getMeta(m);
    var list = (meta.rawList || []).slice();
    list.sort(function (a, b) {
      if (m === "designation") {
        var seqDiff = (Number(a.deg_seq) || 0) - (Number(b.deg_seq) || 0);
        if (seqDiff !== 0) return seqDiff;
      } else {
        var nameDiff = String(a.dept_name || "").localeCompare(
          String(b.dept_name || "")
        );
        if (nameDiff !== 0) return nameDiff;
      }
      return (Number(a[meta.idKey]) || 0) - (Number(b[meta.idKey]) || 0);
    });
    return list;
  }

  function getCategoriesWithFaculty(m) {
    var meta = getMeta(m);
    return getSortedCategories(m).filter(function (cat) {
      return (faculty || []).some(function (f) {
        return String(f[meta.facultyIdKey]) === String(cat[meta.idKey]);
      });
    });
  }

  function countFacultyInFor(m, cat) {
    var meta = getMeta(m);
    return (faculty || []).filter(function (f) {
      return String(f[meta.facultyIdKey]) === String(cat[meta.idKey]);
    }).length;
  }

  var idKey = mode === "designation" ? "deg_id" : "dept_id";
  var nameKey = mode === "designation" ? "deg_name" : "dept_name";
  var facultyIdKey = mode === "designation" ? "fdesignation" : "fdepartment";

  // Full lists for the side menu — one for Department, one for Designation —
  // computed independently of whichever mode is currently active.
  var departmentCategories = useMemo(
    function () {
      return loading ? [] : getCategoriesWithFaculty("department");
    },
    [loading, departments, faculty]
  );

  var designationCategories = useMemo(
    function () {
      return loading ? [] : getCategoriesWithFaculty("designation");
    },
    [loading, designations, faculty]
  );

  var categoriesWithFaculty = useMemo(
    function () {
      return mode === "designation" ? designationCategories : departmentCategories;
    },
    [mode, departmentCategories, designationCategories]
  );

  var activeCategory = catSlug
    ? categoriesWithFaculty.find(function (cat) {
        return slugify(cat[nameKey]) === catSlug;
      })
    : null;

  // Auto-select the first category whenever there's no (valid) category
  // in the URL — covers a bare "/faculty", a bare "/faculty/:mode", and
  // an unknown/stale slug, so the page never shows an empty "pick one"
  // state as long as at least one category has faculty in it.
  useEffect(
    function () {
      if (loading) return;
      if (catSlug && activeCategory) return;

      if (categoriesWithFaculty.length > 0) {
        navigate(
          "/faculty/" + mode + "/" + slugify(categoriesWithFaculty[0][nameKey]),
          { replace: true }
        );
      } else if (catSlug) {
        navigate("/faculty/" + mode, { replace: true });
      }
    },
    [loading, catSlug, activeCategory, categoriesWithFaculty, navigate, mode, nameKey]
  );

  var activeFaculty = activeCategory
    ? (faculty || []).filter(function (f) {
        return String(f[facultyIdKey]) === String(activeCategory[idKey]);
      })
    : [];

  // Accordion: only one of Department / Designation is expanded at a time.
  // Whichever mode the URL points at opens automatically; clicking a
  // heading toggles it (and closes the other one).
  var [openGroup, setOpenGroup] = useState(mode);

  useEffect(
    function () {
      setOpenGroup(mode);
    },
    [mode]
  );

  function toggleGroup(groupMode) {
    setOpenGroup(function (prev) {
      return prev === groupMode ? null : groupMode;
    });
  }

  var deptNameById = useMemo(
    function () {
      var map = {};
      (departments || []).forEach(function (d) {
        map[d.dept_id] = d.dept_name;
      });
      return map;
    },
    [departments]
  );

  var degNameById = useMemo(
    function () {
      var map = {};
      (designations || []).forEach(function (d) {
        map[d.deg_id] = d.deg_name;
      });
      return map;
    },
    [designations]
  );

  // Selecting an item in the side menu both switches mode (if needed)
  // and jumps straight to that category in one navigation.
  function handleCategorySelect(newMode, cat) {
    var meta = getMeta(newMode);
    navigate("/faculty/" + newMode + "/" + slugify(cat[meta.nameKey]));
  }

  function handleImgError(e, name) {
    e.target.src =
      "https://ui-avatars.com/api/?name=" +
      encodeURIComponent(name || "Faculty") +
      "&background=0b5d7a&color=fff&size=150";
  }

  // Row layout: photo | labeled field list | registration-number box —
  // matches the old site's faculty display (Name / Designation / Department
  // / Mobile Number / E-mail / Specialisation, with the reg. no. called out
  // on the right). "Mobile Number" has no backing column in tbl_facultydtls
  // so it renders blank, same as it did on the old site's sample rows.
  function renderFacultyRow(item) {
    var designationName = degNameById[item.fdesignation] || "";
    var departmentName = deptNameById[item.fdepartment] || "";

    return (
      <div className="faculty-row" key={item.faculty_id}>
        <div className="faculty-row-photo-block">
          <div className="faculty-row-photo-wrapper">
            <img
              src={item.fimage}
              alt={item.fname || "Faculty photo"}
              className="faculty-row-photo"
              loading="lazy"
              onError={function (e) {
                handleImgError(e, item.fname);
              }}
            />
          </div>
          <div className="faculty-row-photo-name">{item.fname}</div>
          <div className="faculty-row-photo-designation">
            {designationName}
          </div>
        </div>

        <div className="faculty-row-fields">
          <div className="faculty-detail-line">
            <span className="faculty-detail-label">Name :</span>
            <span className="faculty-detail-value">{item.fname}</span>
          </div>

          <div className="faculty-detail-line">
            <span className="faculty-detail-label">Designation :</span>
            <span className="faculty-detail-value">{designationName}</span>
          </div>

          <div className="faculty-detail-line">
            <span className="faculty-detail-label">Department :</span>
            <span className="faculty-detail-value">{departmentName}</span>
          </div>

          <div className="faculty-detail-line">
            <span className="faculty-detail-label">Mobile Number :</span>
            <span className="faculty-detail-value">&nbsp;</span>
          </div>

          <div className="faculty-detail-line">
            <span className="faculty-detail-label">E-mail :</span>
            <span className="faculty-detail-value">
              {item.femail || <span>&nbsp;</span>}
            </span>
          </div>

          <div className="faculty-detail-line">
            <span className="faculty-detail-label">Specialisation :</span>
            <span className="faculty-detail-value">
              {item.fspecialization || <span>&nbsp;</span>}
            </span>
          </div>

          {item.fspecialinterest ? (
            <div className="faculty-detail-line">
              <span className="faculty-detail-label">Regd. No :</span>
              <span className="faculty-detail-value">
                {item.fspecialinterest}
              </span>
            </div>
          ) : null}
        </div>

        <div className="faculty-row-regno-box">
          <span className="faculty-row-regno-text">{item.fmob}</span>
        </div>
      </div>
    );
  }

  // Renders one accordion group ("Department" or "Designation"). Only the
  // group matching `openGroup` shows its submenu — clicking a heading
  // opens that group and closes the other one (or collapses both if the
  // already-open group is clicked again).
  function renderMenuGroup(groupMode, groupLabel, groupCategories) {
    var isGroupActive = mode === groupMode;
    var isOpen = openGroup === groupMode;

    return (
      <div className="faculty-menu-group" key={groupMode}>
        <button
          type="button"
          className={
            isOpen
              ? "faculty-menu-heading open"
              : "faculty-menu-heading"
          }
          onClick={function () {
            toggleGroup(groupMode);
          }}
        >
          <span className="faculty-menu-heading-label">{groupLabel}</span>
          <span className="faculty-menu-heading-icon">
            {isOpen ? "\u2013" : "+"}
          </span>
        </button>

        {isOpen ? (
          groupCategories.length > 0 ? (
            <ul className="faculty-submenu">
              {groupCategories.map(function (cat) {
                var meta = getMeta(groupMode);
                var isActive =
                  isGroupActive &&
                  activeCategory &&
                  activeCategory[meta.idKey] === cat[meta.idKey];
                var count = countFacultyInFor(groupMode, cat);

                return (
                  <li key={cat[meta.idKey]} className="faculty-submenu-item">
                    <button
                      type="button"
                      className={
                        isActive
                          ? "faculty-submenu-link active"
                          : "faculty-submenu-link"
                      }
                      onClick={function () {
                        handleCategorySelect(groupMode, cat);
                      }}
                    >
                      <span className="faculty-submenu-link-name">
                        {cat[meta.nameKey]}
                      </span>
                      <span className="faculty-submenu-link-count">
                        ({count})
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="faculty-submenu-empty">None found.</p>
          )
        ) : null}
      </div>
    );
  }

  return (
    <main className="content-page-wrapper">
      <div className="content-breadcrumb-wrapper">
        <div className="content-container">
          <div className="content-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i>
              Home
            </Link>
            <span className="breadcrumb-arrow">
              <i className="fa fa-angle-right"></i>
            </span>
            {activeCategory ? (
              <React.Fragment>
                <Link to={"/faculty/" + mode}>Faculty</Link>
                <span className="breadcrumb-arrow">
                  <i className="fa fa-angle-right"></i>
                </span>
                <span className="breadcrumb-current">
                  {activeCategory[nameKey]}
                </span>
              </React.Fragment>
            ) : (
              <span className="breadcrumb-current">Faculty</span>
            )}
          </div>
        </div>
      </div>

      <div className="content-container">
        <div className="content-layout">
          {/* LEFT: "MESSAGE FROM" sidebar, then the Department / Designation
              submenu directly underneath it */}
          <aside className="content-sidebar">
            <div className="faculty-menu-card">
              <div className="faculty-menu-header">
                <div className="faculty-menu-header-icon">
                  <i className="fa fa-graduation-cap"></i>
                </div>
                <span>Faculty</span>
              </div>

              <div className="faculty-menu-body">
                {loading ? (
                  <div className="content-loading">
                    <div className="content-spinner"></div>
                    <p>Loading categories...</p>
                  </div>
                ) : (
                  <React.Fragment>
                    {renderMenuGroup(
                      "department",
                      "View By Department",
                      departmentCategories
                    )}
                    {renderMenuGroup(
                      "designation",
                      "View By Designation",
                      designationCategories
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>

            <div className="official-info-card">
              <div className="official-info-header">
                <div className="official-info-icon">
                  <i className="fa fa-envelope-o"></i>
                </div>
                <span>MESSAGE FROM</span>
              </div>

              <div className="official-info-body">
                {renderOfficialMessage(principal, "DEAN AND PRINCIPAL")}
                {renderOfficialMessage(
                  medicalSuperintendent,
                  "MEDICAL SUPERINTENDENT"
                )}
              </div>
            </div>
          </aside>

          {/* RIGHT: active category's faculty row list */}
          <section className="content-main-card">
            <h1 className="notice-page-title">Faculty</h1>

            <div className="gallery-detail-section">
              {loading || !activeCategory ? (
                <div className="content-loading">
                  <div className="content-spinner"></div>
                </div>
              ) : (
                <React.Fragment>
                  <h2 className="gallery-detail-title">
                    Faculty : {activeCategory[nameKey]}
                  </h2>

                  {loadingFaculty ? (
                    <div className="content-loading">
                      <div className="content-spinner"></div>
                      <p>Loading faculty...</p>
                    </div>
                  ) : activeFaculty.length > 0 ? (
                    <div className="faculty-list">
                      {activeFaculty.map(renderFacultyRow)}
                    </div>
                  ) : (
                    <p className="notice-card-empty">No faculty found.</p>
                  )}
                </React.Fragment>
              )}
            </div>
          </section>
        </div>
      </div>
      {/* Back to top button */}
<BackToTopButton />
    </main>
  );
}
